<template>
  <div class="dashboard">

    <!-- ===== ROW 1: STAT CARDS ===== -->
    <div class="statsRow">

      <!-- Poids -->
      <div class="statCard statCard--poids">
        <div class="statCard__header">
          <div class="statCard__icon"><v-icon size="13">mdi-scale-bathroom</v-icon></div>
          <span class="statCard__label">Poids actuel</span>
        </div>
        <div class="weightDisplay" @click="startEditWeight">
          <template v-if="!editingWeight">
            <span class="weightDisplay__value">{{ actualWeight != null ? actualWeight.toFixed(1) : '—' }}</span>
            <span class="weightDisplay__unit">kg</span>
            <v-icon size="13" class="weightDisplay__edit">mdi-pencil-outline</v-icon>
          </template>
          <template v-else>
            <input v-model.number="newWeight" type="number" step="0.1" min="20" max="300"
              class="weightInput" ref="weightInputRef" @keyup.enter="saveWeight" @blur="saveWeight" @click.stop />
            <span class="weightDisplay__unit">kg</span>
          </template>
        </div>
        <div class="weightBar">
          <div class="weightBar__track">
            <div class="weightBar__fill" :style="{ width: weightProgress + '%' }"></div>
          </div>
          <div class="weightBar__labels">
            <span>{{ formatWeight(toNumber(authStore.userInfos?.originalWeight) || actualWeight) }}</span>
            <span>{{ formatWeight(displayTargetWeight) }}</span>
          </div>
        </div>
      </div>

      <!-- Calories / Macronutriments (carte fusionnée) -->
      <div class="statCard statCard--calories">
        <!-- VUE CALORIES -->
        <template v-if="caloriesSlide === 0">
          <div class="statCard__header">
            <div class="statCard__icon"><v-icon size="13">mdi-fire</v-icon></div>
            <span class="statCard__label">Calories par jour</span>
          </div>
          <div class="calMacroRow">
            <div>
              <div class="statCard__main statCard__main--bottom">
                <span class="statCard__value">{{ targetCalories ? Math.round(targetCalories) : '—' }}</span>
                <span class="statCard__unit">kcal</span>
              </div>
              <p class="statCard__sub">Vos besoins journaliers</p>
            </div>
            <button class="slideArrow" @click="caloriesSlide = 1" title="Voir les macronutriments">›</button>
          </div>
        </template>
        <!-- VUE MACRONUTRIMENTS -->
        <template v-else>
          <div class="statCard__header">
            <div class="statCard__icon"><v-icon size="13">mdi-chart-pie</v-icon></div>
            <span class="statCard__label">Macronutriments</span>
          </div>
          <!-- desktop : tout affiché -->
          <div class="macrosMasterRow">
            <div class="macrosGrid macrosGrid--desktop">
              <div class="macroItem">
                <div class="macroItem__row"><span class="macroItem__val">{{ macros ? Math.round(macros.proteinNeeds) : '—' }}</span><span class="macroItem__unit--plain">g</span></div>
                <span class="macroItem__label">Prot.</span>
              </div>
              <div class="macroItem">
                <div class="macroItem__row"><span class="macroItem__val">{{ macros ? Math.round(macros.carbNeeds) : '—' }}</span><span class="macroItem__unit--plain">g</span></div>
                <span class="macroItem__label">Gluc.</span>
              </div>
              <div class="macroItem">
                <div class="macroItem__row"><span class="macroItem__val">{{ macros ? Math.round(macros.fatNeeds) : '—' }}</span><span class="macroItem__unit--plain">g</span></div>
                <span class="macroItem__label">Lip.</span>
              </div>
              <div class="macroItem">
                <div class="macroItem__row"><span class="macroItem__val">30</span><span class="macroItem__unit--plain">g</span></div>
                <span class="macroItem__label">Fibres</span>
              </div>
            </div>
            <button class="slideArrow" @click="caloriesSlide = 0" title="Retour aux calories">‹</button>
          </div>
          <!-- mobile : carousel -->
          <div class="macrosCarousel">
            <button class="macrosCarousel__arrow" @click="macroIdx = (macroIdx - 1 + 4) % 4">‹</button>
            <div class="macroItem" style="flex:1;align-items:center;">
              <div class="macroItem__row">
                <span class="macroItem__val">{{ macroItems[macroIdx].val }}</span>
                <span class="macroItem__unit--plain">g</span>
              </div>
              <span class="macroItem__label">{{ macroItems[macroIdx].label }}</span>
            </div>
            <button class="macrosCarousel__arrow" @click="macroIdx = (macroIdx + 1) % 4">›</button>
          </div>
        </template>
      </div>
      <!-- Menus planifiés -->
      <div class="statCard statCard--macros">
        <div class="statCard__header">
          <div class="statCard__icon"><v-icon size="13">mdi-calendar-check-outline</v-icon></div>
          <span class="statCard__label">Menus planifiés</span>
        </div>
        <div class="menusContent">
          <div class="statCard__main statCard__main--bottom">
            <span class="statCard__value">{{ menusCompleted }}</span>
            <span class="statCard__unit">/ {{ menusTotal }}</span>
          </div>
          <p class="statCard__sub">Plus que {{ Math.max(0, menusTotal - menusCompleted) }} menus</p>
        </div>
      </div>

      <!-- Hashtags profil -->
      <div class="statCard statCard--tags">
        <div class="statCard__header" style="justify-content:space-between;">
          <div style="display:flex;align-items:center;gap:8px;">
            <div class="statCard__icon"><v-icon size="13">mdi-tag-multiple-outline</v-icon></div>
            <span class="statCard__label">Mon profil</span>
          </div>
          <button class="statCard__editBtn" @click="router.push('/profile')" title="Modifier mon profil">
            <v-icon size="13">mdi-pencil-outline</v-icon>
          </button>
        </div>
        <div class="profileTags">
          <span v-for="tag in profileTags" :key="tag.label" class="profileTag" :style="{ background: tag.bg, color: tag.color }">
            #{{ tag.label }}
          </span>
          <span v-if="!profileTags.length" class="statCard__sub">Complétez votre profil</span>
        </div>
      </div>

      <!-- Prochain repas -->
      <div class="statCard statCard--next">
        <div class="statCard__header">
          <div class="statCard__icon"><v-icon size="13">mdi-silverware-fork-knife</v-icon></div>
          <span class="statCard__label">Prochain repas</span>
        </div>
        <div v-if="nextMeal" class="nextMeal">
          <div class="nextMeal__img">
            <img v-if="nextMeal.recipe.url || nextMeal.recipe.imageUrl" :src="nextMeal.recipe.url || nextMeal.recipe.imageUrl" :alt="nextMeal.recipe.title" />
            <v-icon v-else size="22">mdi-silverware-fork-knife</v-icon>
          </div>
          <div class="nextMeal__info">
            <p class="nextMeal__title">{{ nextMeal.recipe.title }}</p>
            <p class="nextMeal__kcal">{{ Math.round((nextMeal.recipe.totals?.kcal || 0) / (nextMeal.recipe.servings || 1)) }} kcal</p>
            <div class="nextMeal__tags" v-if="nextMeal.recipe.tags && nextMeal.recipe.tags.length">
              <span v-for="tag in nextMeal.recipe.tags.slice(0,2)" :key="tag" class="nextMeal__tag">{{ tag }}</span>
            </div>
          </div>
        </div>
        <div v-else class="statCard__sub" style="margin-top:auto">Générez un menu</div>
      </div>

    </div>

  </div>

    <!-- ===== ROW 2 ===== -->
    <div class="mainRow">

      <!-- LEFT -->
      <div class="leftCol">
        <div class="card card--calendar">
          <div class="card__header">
            <div class="card__header__left"><v-icon size="16" class="card__icon">mdi-calendar-week</v-icon><p class="card__title">Menu de la semaine</p></div>
            <button class="linkBtn" @click="router.push('/menu')">Gérer <v-icon size="13">mdi-chevron-right</v-icon></button>
          </div>
          <div class="weekCalendar">
            <div v-for="(day, idx) in weekDays" :key="idx" class="dayPill"
              :class="{ 'dayPill--today': idx === todayIndex, 'dayPill--selected': idx === selectedDayIndex }"
              @click="selectedDayIndex = idx">
              <span class="dayPill__name">{{ day.short }}</span>
              <span class="dayPill__num">{{ day.num }}</span>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card__header">
            <div class="card__header__left"><v-icon size="16" class="card__icon">mdi-silverware-fork-knife</v-icon><p class="card__title">{{ weekDays[selectedDayIndex] && weekDays[selectedDayIndex].full }}</p></div>
            <span class="card__badge" v-if="selectedDayMenu">{{ selectedDayTotals }} kcal</span>
          </div>
          <div v-if="isLoadingMenu" class="loadingArea">
            <v-progress-circular indeterminate color="accent" size="24" />
          </div>
          <div v-else-if="!menusSaved" class="emptyState">
            <v-icon size="32" class="mb-2">mdi-calendar-blank-outline</v-icon>
            <p>Aucun menu généré</p>
            <button class="linkBtn mt-1" @click="router.push('/menu')">Générer un menu</button>
          </div>
          <div v-else-if="!selectedDayMenu || !Object.keys(selectedDayMenu).length" class="emptyState">
            <p>Aucun repas pour ce jour</p>
          </div>
          <div v-else class="mealList">
            <div v-for="mealName in orderedMealKeys(selectedDayMenu)" :key="mealName" class="mealSection">
              <p class="mealSection__name">{{ $t(mealName) }}</p>
              <div v-for="recipe in selectedDayMenu[mealName]" :key="recipe.title" class="mealRow">
                <div class="mealRow__img">
                  <img v-if="recipe.url || recipe.imageUrl" :src="recipe.url || recipe.imageUrl" :alt="recipe.title" />
                  <v-icon v-else size="16">mdi-silverware-fork-knife</v-icon>
                </div>
                <span class="mealRow__name">{{ recipe.title }}</span>
                <span class="mealRow__kcal">{{ Math.round((recipe.totals && recipe.totals.kcal || 0) / (recipe.servings || 1)) }} kcal</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MIDDLE -->
      <div class="middleCol">
        <div class="card card--courses">
          <div class="card__header">
            <div class="card__header__left">
              <v-icon size="16" class="card__icon">mdi-cart-outline</v-icon>
              <p class="card__title">Liste de courses</p>
            </div>
            <div class="card__header__right">
              <button class="linkBtn" @click="router.push('/shopping_list')">Voir tout <v-icon size="13">mdi-chevron-right</v-icon></button>
              <button class="addItemBtn" @click="showAddItem = !showAddItem" title="Ajouter un article">
                <v-icon size="14">mdi-plus</v-icon>
              </button>
            </div>
          </div>
          <div class="toggleGroup mb-2">
            <button :class="['toggleBtn', coursesView === 'day' && 'toggleBtn--active']" @click="coursesView = 'day'">Jour</button>
            <button :class="['toggleBtn', coursesView === 'week' && 'toggleBtn--active']" @click="coursesView = 'week'">Semaine</button>
          </div>
          <div v-if="showAddItem" class="addItemRow">
            <input v-model="newItemName" class="addItemInput" placeholder="Nom de l'article..." @keyup.enter="addCourseItem" />
            <button class="addItemConfirm" @click="addCourseItem"><v-icon size="14">mdi-check</v-icon></button>
          </div>
          <div v-if="isLoadingCourses" class="loadingArea">
            <v-progress-circular indeterminate color="accent" size="24" />
          </div>
          <div v-else-if="!shoppingItems.length" class="emptyState">
            <v-icon size="32" class="mb-2">mdi-cart-outline</v-icon>
            <p>Aucune course pour l'instant</p>
            <button class="linkBtn mt-1" @click="router.push('/shopping_list')">Voir les courses</button>
          </div>
          <div v-else class="coursesList">
            <div v-for="(item, idx) in shoppingItems" :key="idx" class="courseItem"
              :class="{ 'courseItem--checked': item.checked }" @click="toggleDashboardItem(idx)">
              <v-icon size="14" class="courseItem__icon">{{ item.checked ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}</v-icon>
              <span class="courseItem__name">{{ item.name || item.productName }}</span>
              <span class="courseItem__qty" v-if="item.grams || item.quantity">{{ formatQty(item) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="rightCol">

        <!-- Bloc recette aléatoire -->
        <div class="randomCard" :class="{ 'randomCard--spinning': isSpinning, 'randomCard--has-result': !!randomRecipe && !isSpinning }">
          <!-- Image décorative fixe -->
          <div class="randomCard__decoBg"></div>
          <div class="randomCard__glass"></div>

          <div class="randomCard__content">

            <!-- État vide -->
            <template v-if="!randomRecipe && !isSpinning">
              <!-- Filtres chips transparents -->
              <div class="randomCard__filters">
                <span
                  v-for="f in mealFilters.filter(f => f.value !== 'all')" :key="f.value"
                  class="randomCard__filterChip"
                  :class="{ 'randomCard__filterChip--active': randomMealFilter === f.value }"
                  @click.stop="randomMealFilter = f.value"
                >{{ f.label }}</span>
              </div>
              <button class="randomCard__spinBtn" @click.stop="pickRandom" :disabled="isLoadingBaseRecipes">
                <v-icon size="14">mdi-shuffle-variant</v-icon> Tirer une recette au sort
              </button>
            </template>

            <!-- Spinning -->
            <template v-else-if="isSpinning">
              <div class="randomCard__spinState">
                <p class="randomCard__spinLabel">Tirage en cours…</p>
              </div>
            </template>

            <!-- Résultat -->
            <template v-else>
              <div class="randomCard__resultWrap" @click.stop="openRandomRecipeDialog">
                <div class="randomCard__resultImg" v-if="getBaseRecipeImage(randomRecipe)">
                  <img :src="getBaseRecipeImage(randomRecipe)" :alt="randomRecipe.title" />
                </div>
                <div class="randomCard__resultImg randomCard__resultImg--empty" v-else>
                  <v-icon size="22" style="opacity:.35">mdi-food-variant</v-icon>
                </div>
                <div class="randomCard__resultInfo">
                  <p class="randomCard__resultEyebrow">{{ mealFilters.find(f => f.value === randomMealFilter)?.emoji }} Suggestion du jour</p>
                  <p class="randomCard__resultName">{{ randomRecipe.title }}</p>
                  <div class="randomCard__resultTags">
                    <span class="randomCard__tag">{{ translateCategory(randomRecipe.category) }}</span>
                    <span class="randomCard__tag randomCard__tag--kcal">{{ randomRecipe.totals?.kcal || 0 }} kcal</span>
                  </div>
                </div>
              </div>
              <button class="randomCard__reroll" @click.stop="pickRandom">
                Autre recette
              </button>
            </template>

          </div>
        </div>

        <!-- Dernières recettes -->
        <div class="card">
          <div class="card__header">
            <div class="card__header__left"><v-icon size="16" class="card__icon">mdi-book-open-outline</v-icon><p class="card__title">Dernières recettes</p></div>
            <button class="linkBtn" @click="router.push('/my_recipes')">Voir tout <v-icon size="13">mdi-chevron-right</v-icon></button>
          </div>
          <div v-if="isLoadingRecipes" class="loadingArea">
            <v-progress-circular indeterminate color="accent" size="24" />
          </div>
          <div v-else-if="!latestRecipes.length" class="emptyState emptyState--sm">
            <p>Aucune recette</p>
          </div>
          <div v-else class="recipeList">
            <div v-for="item in latestRecipes" :key="item.id" class="recipeRow" @click="router.push('/my_recipes')">
              <div class="recipeRow__img">
                <img v-if="getRecipeImage(item)" :src="getRecipeImage(item)" :alt="item.title" />
                <v-icon v-else size="18">mdi-silverware-fork-knife</v-icon>
              </div>
              <div class="recipeRow__info">
                <p class="recipeRow__name">{{ item.title || 'Recette' }}</p>
                <div class="recipeRow__tags">
                  <span class="tag tag--cat">{{ translateCategory(item.category) }}</span>
                  <span class="tag tag--kcal"><v-icon size="10">mdi-fire</v-icon> {{ item.totals && item.totals.kcal || 0 }} kcal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══ POP-UP recette aléatoire ══ -->
      <v-dialog v-model="randomRecipeDialog" max-width="820" scrollable>
        <v-card v-if="randomRecipe" rounded="xl" color="secondary" class="rdialog">
          <!-- Header image + titre -->
          <div class="rdialog__hero">
            <div class="rdialog__imgWrap">
              <img v-if="getBaseRecipeImage(randomRecipe)" :src="getBaseRecipeImage(randomRecipe)" class="rdialog__img" :alt="randomRecipe.title" />
              <div v-else class="rdialog__imgPlaceholder"><v-icon size="40" style="opacity:.3">mdi-food-variant</v-icon></div>
            </div>
            <div class="rdialog__heroInfo">
              <div class="rdialog__eyebrow">Recette de base</div>
              <h2 class="rdialog__title">{{ randomRecipe.title }}</h2>
              <div class="rdialog__tags">
                <span v-if="randomRecipe.category" class="rdialog__tag rdialog__tag--accent">{{ translateCategory(randomRecipe.category) }}</span>
                <span v-if="randomRecipe.diet" class="rdialog__tag">{{ randomRecipe.diet }}</span>
              </div>
              <!-- Macros rapides -->
              <div class="rdialog__macros">
                <div class="rdialog__macroItem">
                  <span class="rdialog__macroVal">{{ randomRecipe.totals?.kcal ?? 0 }}</span>
                  <span class="rdialog__macroLabel">kcal</span>
                </div>
                <div class="rdialog__macroItem">
                  <span class="rdialog__macroVal">{{ Math.round(randomRecipe.totals?.protein ?? 0) }}g</span>
                  <span class="rdialog__macroLabel">prot.</span>
                </div>
                <div class="rdialog__macroItem">
                  <span class="rdialog__macroVal">{{ Math.round(randomRecipe.totals?.carbs ?? 0) }}g</span>
                  <span class="rdialog__macroLabel">gluc.</span>
                </div>
                <div class="rdialog__macroItem">
                  <span class="rdialog__macroVal">{{ Math.round(randomRecipe.totals?.fat ?? 0) }}g</span>
                  <span class="rdialog__macroLabel">lip.</span>
                </div>
              </div>
              <!-- Actions -->
              <div class="rdialog__actions">
                <button class="rdialog__btn rdialog__btn--primary" :disabled="addingToMyRecipes" @click="addRandomToMyRecipes">
                  <v-progress-circular v-if="addingToMyRecipes" indeterminate size="14" width="2" color="white" class="mr-1" />
                  <v-icon v-else size="15" class="mr-1">mdi-plus</v-icon>
                  {{ addedToMyRecipes ? 'Ajoutée ✓' : 'Ajouter à mes recettes' }}
                </button>
                <button class="rdialog__btn rdialog__btn--secondary" @click="pickRandom">
                  <v-icon size="14" class="mr-1">mdi-refresh</v-icon> Autre recette
                </button>
                <button class="rdialog__closeBtn" @click="randomRecipeDialog = false">
                  <v-icon size="18">mdi-close</v-icon>
                </button>
              </div>
            </div>
          </div>
          <!-- Ingrédients -->
          <v-card-text class="rdialog__body">
            <div v-if="randomRecipe.ingredients?.length" class="rdialog__section">
              <p class="rdialog__sectionTitle">Ingrédients <span class="rdialog__sectionCount">{{ randomRecipe.ingredients.length }}</span></p>
              <div class="rdialog__ingGrid">
                <div v-for="(ing, i) in randomRecipe.ingredients" :key="i" class="rdialog__ing">
                  <div class="rdialog__ingCircle">
                    <img v-if="ing.productImage" :src="ing.productImage" class="rdialog__ingImg" />
                    <v-icon v-else size="22" style="opacity:.3">mdi-food-variant</v-icon>
                  </div>
                  <span class="rdialog__ingName">{{ ing.name || ing.productName }}</span>
                  <span class="rdialog__ingQty">{{ ing.grams }}g</span>
                </div>
              </div>
            </div>
            <!-- Étapes -->
            <div v-if="randomRecipe.steps?.length" class="rdialog__section">
              <p class="rdialog__sectionTitle">Préparation</p>
              <div class="rdialog__steps">
                <div v-for="(step, i) in randomRecipe.steps" :key="i" class="rdialog__step">
                  <div class="rdialog__stepNum">{{ i + 1 }}</div>
                  <div class="rdialog__stepText" v-html="step" />
                </div>
              </div>
            </div>
            <div v-if="!randomRecipe.ingredients?.length && !randomRecipe.steps?.length" class="emptyState">
              <p>Aucun détail disponible pour cette recette.</p>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

    <v-snackbar v-model="weightSnack.show" :timeout="2500" :color="weightSnack.color">{{ weightSnack.text }}</v-snackbar>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth_store";
import { getUserRecipes } from "@/service/recipe_service";
import { getUserWeeklyMenu } from "@/service/menu_storage_service";
import { getUserShoppingLists } from "@/service/shopping_list_service";
import { getBaseMetabolicRate, getDailyNeeds, getTargetCalories, calculateMacros } from "@/service/health_service";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";

const DAY_SHORT = ['Lun.','Mar.','Mer.','Jeu.','Ven.','Sam.','Dim.'];
const DAY_FULL  = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];
const ARC_TOTAL = 220; // arc length for our SVG path

export default {
  name: "HomeBlock",
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();
    const recipes = ref([]);
    const isLoadingRecipes = ref(false);
    const isLoadingMenu = ref(false);
    const isLoadingCourses = ref(false);
    const weeklyMenu = ref(null);
    const shoppingLists = ref({});
    const coursesView = ref('day');
    const selectedDayIndex = ref(0);
    const editingWeight = ref(false);
    const newWeight = ref(null);
    const weightInputRef = ref(null);
    const weightSnack = ref({ show: false, text: '', color: 'success' });

    const randomRecipe = ref(null);
    const isSpinning = ref(false);
    const baseRecipes = ref([]);
    const isLoadingBaseRecipes = ref(false);
    const randomMealFilter = ref('all');
    const randomRecipeDialog = ref(false);
    const addingToMyRecipes = ref(false);
    const addedToMyRecipes = ref(false);

    const mealFilters = [
      { value: 'all',       label: 'Tout',      emoji: '🍽️',  categories: [] },
      { value: 'breakfast', label: 'Petit-déj', emoji: '🌅',  categories: ['Breakfast'] },
      { value: 'snack',     label: 'Collation', emoji: '🍎',  categories: ['Snack', 'Snacks'] },
      { value: 'meal',      label: 'Plat',      emoji: '🍲',  categories: ['Meal', 'Starter'] },
      { value: 'dessert',   label: 'Dessert',   emoji: '🍰',  categories: ['Dessert'] },
    ];

    const filteredBaseRecipes = computed(() => {
      const filter = mealFilters.find(f => f.value === randomMealFilter.value);
      if (!filter || !filter.categories.length) return baseRecipes.value;
      return baseRecipes.value.filter(r => filter.categories.includes(r.category));
    });

    const getBaseRecipeImage = (item) => item && (item.imageUrl || item.url || item.image) || '';

    const pickRandom = () => {
      const pool = filteredBaseRecipes.value;
      if (!pool.length) return;
      isSpinning.value = true;
      randomRecipe.value = null;
      addedToMyRecipes.value = false;
      setTimeout(() => {
        const idx = Math.floor(Math.random() * pool.length);
        randomRecipe.value = pool[idx];
        isSpinning.value = false;
      }, 700);
    };

    const openRandomRecipeDialog = () => {
      if (randomRecipe.value) {
        addedToMyRecipes.value = false;
        randomRecipeDialog.value = true;
      }
    };

    const addRandomToMyRecipes = async () => {
      if (!randomRecipe.value || !authStore.userid) return;
      addingToMyRecipes.value = true;
      try {
        const { addBaseRecipeToUser } = await import('@/service/base_recipe_service');
        const result = await addBaseRecipeToUser(authStore.userid, randomRecipe.value, randomRecipe.value.id);
        addedToMyRecipes.value = true;
        if (result.existed) {
          // déjà dans la collection, on le signale quand même positivement
        }
      } catch (e) {
        console.error(e);
      } finally {
        addingToMyRecipes.value = false;
      }
    };

    const today = new Date();
    const dow = today.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    const weekDays = computed(() => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const months = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
      return { short: DAY_SHORT[i], full: DAY_FULL[i] + ' ' + d.getDate() + ' ' + months[d.getMonth()], num: d.getDate(), date: d };
    }));

    const todayIndex = computed(() => {
      const tn = today.getDate(), tm = today.getMonth();
      return weekDays.value.findIndex(d => d.date.getDate() === tn && d.date.getMonth() === tm);
    });

    const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };

    const actualWeight = computed(() => toNumber(authStore.userInfos?.actualWeight));
    const targetWeight = computed(() => toNumber(authStore.userInfos?.targetWeight));
    const originalWeight = computed(() => toNumber(authStore.userInfos?.originalWeight));
    const displayTargetWeight = computed(() => targetWeight.value ?? actualWeight.value);

    // Arc de cercle poids
    const weightProgress = computed(() => {
      const w = actualWeight.value, t = targetWeight.value, o = originalWeight.value || w;
      if (!w || !t || !o) return 0;
      const total = Math.abs(t - o);
      if (total === 0) return 100;
      return Math.min(100, Math.round((Math.abs(w - o) / total) * 100));
    });

    const arcLength = computed(() => ARC_TOTAL);
    const arcDashOffset = computed(() => {
      const w = actualWeight.value, t = targetWeight.value, o = originalWeight.value || w;
      if (!w || !t || !o) return ARC_TOTAL;
      const totalDelta = Math.abs(t - o);
      if (totalDelta === 0) return 0;
      const done = Math.abs(w - o);
      const progress = Math.min(1, done / totalDelta);
      return ARC_TOTAL - (ARC_TOTAL * progress);
    });

    const targetCalories = computed(() => {
      const u = authStore.userInfos;
      if (!u) return null;
      const w = toNumber(u.actualWeight), h = toNumber(u.actualSize), nap = toNumber(u.NAP) || 1.4;
      if (!w || !h) return null;
      return getTargetCalories(getDailyNeeds(getBaseMetabolicRate(u.age, h, w, u.sex), nap), u.weightObjective);
    });

    const macros = computed(() => {
      const u = authStore.userInfos;
      if (!u || !targetCalories.value) return null;
      const w = toNumber(u.actualWeight);
      if (!w) return null;
      return calculateMacros(w, u.activityLevel, u.weightObjective, targetCalories.value);
    });

    // Menus planifiés
    const menusTotal = computed(() => {
      const w = actualWeight.value, t = targetWeight.value;
      if (!w || !t) return 20;
      const delta = Math.abs(t - w);
      const months = Math.ceil(delta / 1.5) || 1;
      return months * 4;
    });
    const menusCompleted = computed(() => menusSaved.value ? 1 : 0);

    const MEAL_ORDER = ['breakfast', 'lunch', 'snack', 'dinner'];
    const orderedMealKeys = (menu) => {
      if (!menu) return [];
      const keys = Object.keys(menu);
      return [...MEAL_ORDER.filter(k => keys.includes(k)), ...keys.filter(k => !MEAL_ORDER.includes(k))];
    };

    const TAG_COLORS = [
      { bg: 'rgba(117,83,252,0.1)', color: '#7553FC' },
      { bg: 'rgba(34,197,94,0.1)', color: '#16a34a' },
      { bg: 'rgba(249,115,22,0.1)', color: '#ea6c0b' },
      { bg: 'rgba(236,72,153,0.1)', color: '#db2777' },
      { bg: 'rgba(14,165,233,0.1)', color: '#0284c7' },
      { bg: 'rgba(234,179,8,0.1)', color: '#ca8a04' },
    ];

    const profileTags = computed(() => {
      const u = authStore.userInfos;
      if (!u) return [];
      const tags = [];

      // 1. Objectif principal
      if (u.weightObjective) {
        const map = { loseWeight: 'Perte de poids', gainWeight: 'Prise de masse', maintainWeight: 'Maintien' };
        if (map[u.weightObjective]) tags.push(map[u.weightObjective]);
      }

      // 2. Régime alimentaire (string ou tableau)
      const dietMap = { vegan: 'Végan', vegetarian: 'Végétarien', flexitarian: 'Flexitarien', pescatarian: 'Pescatarien', halal: 'Halal', kosher: 'Casher' };
      const diets = Array.isArray(u.diet) ? u.diet : (u.diet ? [u.diet] : []);
      diets.filter(d => d && d !== 'none' && d !== 'omnivore').forEach(d => tags.push(dietMap[d] || d));

      // 3. Intolérances
      if (Array.isArray(u.intolerances)) u.intolerances.filter(i => i && i !== 'none' && i !== 'preferNotToAnswer').forEach(i => {
        const map = { gluten: 'Sans gluten', lactose: 'Sans lactose', nuts: 'Sans noix', eggs: 'Sans œufs', soy: 'Sans soja', fish: 'Sans poisson' };
        tags.push(map[i] || i);
      });

      // 4. Allergies
      if (Array.isArray(u.allergies)) u.allergies.filter(a => a && a !== 'none' && a !== 'preferNotToAnswer').forEach(a => {
        const map = { peanuts: 'Cacahuètes', nuts: 'Noix', fish: 'Poisson', eggs: 'Œufs', shellfish: 'Crustacés', milk: 'Lait', wheat: 'Blé', soy: 'Soja' };
        tags.push(map[a] || a);
      });

      // 5. Conditions médicales
      if (Array.isArray(u.medicalConditions)) u.medicalConditions.filter(c => c && c !== 'none' && c !== 'preferNotToAnswer').forEach(c => {
        const map = {
          diabetes: 'Diabète', hypertension: 'Hypertension', cholesterol: 'Cholestérol',
          ibs: 'Côlon irritable', celiac: 'Cœliaque', anxiety: 'Anxiété', depression: 'Dépression',
          crohndisease: 'Crohn', thyroid: 'Thyroïde', pcos: 'SOPK', endometriosis: 'Endométriose',
          arthritis: 'Arthrite', gerd: 'RGO',
        };
        const key = c.toLowerCase().replace(/[^a-z]/g, '');
        const translated = map[key] || (key.includes('bilia') || key.includes('vesicul') ? 'Vésicule biliaire' : c);
        tags.push(translated);
      });

      return tags.slice(0, 8).map((label, i) => ({ label, ...TAG_COLORS[i % TAG_COLORS.length] }));
    });

    const caloriesSlide = ref(0);
    const macroIdx = ref(0);
    const macroItems = computed(() => [
      { label: 'Protéines', val: macros.value ? Math.round(macros.value.proteinNeeds) : '—' },
      { label: 'Glucides',  val: macros.value ? Math.round(macros.value.carbNeeds)    : '—' },
      { label: 'Lipides',   val: macros.value ? Math.round(macros.value.fatNeeds)     : '—' },
      { label: 'Fibres',    val: 30 },
    ]);

    const formatWeight = (v) => v == null ? '--' : `${v.toFixed(1)} kg`;

    const formatQty = (item) => {
      const g = item.grams || 0;
      if (g >= 1000) return `${(g / 1000).toFixed(1)} kg`;
      if (g > 0) return `${g}g`;
      return item.quantity ? `${item.quantity} ${item.unit || ''}`.trim() : '';
    };

    const startEditWeight = async () => {
      newWeight.value = actualWeight.value;
      editingWeight.value = true;
      await nextTick();
      weightInputRef.value?.focus();
    };

    const saveWeight = async () => {
      if (!editingWeight.value) return;
      editingWeight.value = false;
      const w = newWeight.value;
      if (!w || w === actualWeight.value || w < 20 || w > 300) return;
      try {
        const docRef = doc(db, 'User', authStore.userid);
        const snap = await getDoc(docRef);
        await setDoc(docRef, { ...(snap.exists() ? snap.data() : {}), actualWeight: w }, { merge: true });
        if (authStore.userInfos) authStore.userInfos.actualWeight = w;
        weightSnack.value = { show: true, text: `Poids mis à jour : ${w} kg ✅`, color: 'success' };
      } catch (e) {
        weightSnack.value = { show: true, text: 'Erreur lors de la mise à jour', color: 'error' };
      }
    };

    const nextMeal = computed(() => {
      if (!weeklyMenu.value?.daily) return null;
      const todayIdx = todayIndex.value >= 0 ? todayIndex.value : 0;
      const dayMenu = weeklyMenu.value.daily[todayIdx]?.menu;
      if (!dayMenu) return null;
      const now = new Date();
      const hour = now.getHours();
      const min = now.getMinutes();
      const timeInMin = hour * 60 + min;
      // Petit-déj: <10h et ≥22h | Déjeuner: 10h-13h30 | Collation: 13h30-17h | Dîner: 17h-22h
      let order;
      if (timeInMin < 600 || timeInMin >= 1320) {
        order = ['breakfast','lunch','snack','dinner'];
      } else if (timeInMin < 810) {
        order = ['lunch','snack','dinner','breakfast'];
      } else if (timeInMin < 1020) {
        order = ['snack','dinner','breakfast','lunch'];
      } else {
        order = ['dinner','breakfast','lunch','snack'];
      }
      for (const mealType of order) {
        const meals = dayMenu[mealType];
        if (meals && meals.length) return { mealType, recipe: meals[0] };
      }
      // fallback: first meal
      const first = Object.values(dayMenu)[0];
      return first && first.length ? { mealType: Object.keys(dayMenu)[0], recipe: first[0] } : null;
    });

    const menusSaved = computed(() => !!(weeklyMenu.value && weeklyMenu.value.daily && weeklyMenu.value.daily.length));
    const selectedDayMenu = computed(() => weeklyMenu.value && weeklyMenu.value.daily ? weeklyMenu.value.daily[selectedDayIndex.value] && weeklyMenu.value.daily[selectedDayIndex.value].menu || null : null);
    const selectedDayTotals = computed(() => weeklyMenu.value && weeklyMenu.value.daily ? Math.round((weeklyMenu.value.daily[selectedDayIndex.value] && weeklyMenu.value.daily[selectedDayIndex.value].totals && weeklyMenu.value.daily[selectedDayIndex.value].totals.kcal) || 0) : 0);

    const shoppingItems = computed(() => {
      const lists = Object.values(shoppingLists.value);
      if (!lists.length) return [];
      const menuList = lists.find(l => l.generatedFromMenu);
      if (!menuList) return [];

      if (coursesView.value === 'week') {
        return menuList.items || [];
      }

      // Vue "Jour" : recalculer les ingrédients du jour sélectionné
      const dayMenu = selectedDayMenu.value;
      if (!dayMenu) return [];
      const ingredientsMap = {};
      for (const meals of Object.values(dayMenu)) {
        for (const recipe of meals) {
          if (!recipe.ingredients) continue;
          for (const ing of recipe.ingredients) {
            const name = ing.name || ing.productName || '';
            if (!name) continue;
            const key = name.toLowerCase().trim();
            if (ingredientsMap[key]) {
              ingredientsMap[key].grams = (ingredientsMap[key].grams || 0) + (ing.grams || 0);
            } else {
              ingredientsMap[key] = { name, grams: ing.grams || 0, quantity: '1', checked: false };
            }
          }
        }
      }
      return Object.values(ingredientsMap);
    });

    const toTimestamp = (raw) => {
      if (!raw) return null;
      if (typeof raw === 'number') return raw;
      if (typeof raw === 'string') { const p = Date.parse(raw); return Number.isNaN(p) ? null : p; }
      if (typeof raw === 'object' && raw.seconds) return raw.seconds * 1000;
      return null;
    };

    const selectLatest = (items, fields) => [...items.map((item, index) => {
      const rawDate = fields.map(f => item && item[f]).find(v => v !== undefined && v !== null);
      return { item, index, ts: toTimestamp(rawDate) };
    })].sort((a, b) => { if (a.ts && b.ts) return b.ts - a.ts; if (a.ts) return -1; if (b.ts) return 1; return b.index - a.index; }).slice(0, 5).map(e => e.item);

    const latestRecipes = computed(() => selectLatest(recipes.value, ['createdAt', 'updatedAt']));
    const getRecipeImage = (item) => item && (item.url || item.image || item.imageUrl) || '';

    const categoryMap = { 'Meal': 'Plat', 'Starter': 'Entrée', 'Dessert': 'Dessert', 'Breakfast': 'Petit-déjeuner', 'Snacks': 'Collation', 'Snack': 'Collation' };
    const translateCategory = (cat) => cat ? (categoryMap[cat] || cat) : 'Sans catégorie';

    const toggleDashboardItem = async (idx) => {
      const lists = Object.values(shoppingLists.value);
      if (!lists.length) return;
      const latest = lists.find(l => l.generatedFromMenu);
      if (!latest || !latest.id) return;
      const items = [...(latest.items || [])];
      items[idx] = { ...items[idx], checked: !items[idx].checked };
      try {
        const { updateShoppingList } = await import('@/service/shopping_list_service');
        await updateShoppingList(authStore.userid, latest.id, { ...latest, items });
        shoppingLists.value = { ...shoppingLists.value, [latest.id]: { ...latest, items } };
      } catch(e) { console.error(e); }
    };

    onMounted(() => {
      if (todayIndex.value >= 0) selectedDayIndex.value = todayIndex.value;
      // Charger les recettes de base pour le tirage aléatoire
      (async () => {
        isLoadingBaseRecipes.value = true;
        try {
          const { getBaseRecipes } = await import('@/service/base_recipe_service');
          const map = await getBaseRecipes();
          baseRecipes.value = Object.entries(map).map(([id, v]) => ({ id, ...v }));
        } catch(e) { console.error(e); } finally { isLoadingBaseRecipes.value = false; }
      })();
      watch(() => authStore.userInfos, async () => {
        if (!authStore || !authStore.userid) return;
        isLoadingRecipes.value = true;
        try { const d = await getUserRecipes(authStore.userid); recipes.value = Object.entries(d).map(([id, v]) => ({ id, ...v })); } catch(e){console.error(e);} finally { isLoadingRecipes.value = false; }
        isLoadingMenu.value = true;
        try { const s = await getUserWeeklyMenu(authStore.userid); weeklyMenu.value = s && s.weeklyMenu || null; } catch(e){console.error(e);} finally { isLoadingMenu.value = false; }
        isLoadingCourses.value = true;
        try {
          shoppingLists.value = await getUserShoppingLists(authStore.userid);
          // Synchroniser la liste depuis le menu si un menu est sauvegardé
          if (weeklyMenu.value && weeklyMenu.value.daily) {
            const { syncMenuShoppingList } = await import('@/service/shopping_list_service');
            const result = await syncMenuShoppingList(authStore.userid, weeklyMenu.value);
            if (result) {
              // Recharger les listes pour refléter la mise à jour
              shoppingLists.value = await getUserShoppingLists(authStore.userid);
            }
          }
        } catch(e){console.error(e);} finally { isLoadingCourses.value = false; }
      }, { immediate: true });
    });

    const showAddItem = ref(false);
    const newItemName = ref('');

    const addCourseItem = async () => {
      const name = newItemName.value.trim();
      if (!name) return;
      const lists = Object.values(shoppingLists.value);
      const menuList = lists.find(l => l.generatedFromMenu);
      const target = menuList || [...lists].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0];
      if (!target || !target.id) return;
      const items = [...(target.items || []), { name, grams: 0, quantity: '1', checked: false }];
      try {
        const { updateShoppingList } = await import('@/service/shopping_list_service');
        await updateShoppingList(authStore.userid, target.id, { ...target, items });
        shoppingLists.value = { ...shoppingLists.value, [target.id]: { ...target, items } };
        newItemName.value = '';
        showAddItem.value = false;
      } catch(e) { console.error(e); }
    };

    return { router, authStore, translateCategory, nextMeal, weekDays, todayIndex, selectedDayIndex, showAddItem, newItemName, addCourseItem, isLoadingRecipes, isLoadingMenu, isLoadingCourses, latestRecipes, getRecipeImage, actualWeight, targetWeight, displayTargetWeight, weightProgress, targetCalories, macros, formatWeight, formatQty, menusSaved, selectedDayMenu, selectedDayTotals, shoppingItems, coursesView, arcLength, arcDashOffset, editingWeight, newWeight, weightInputRef, startEditWeight, saveWeight, weightSnack, toggleDashboardItem, menusTotal, menusCompleted, toNumber, orderedMealKeys, profileTags, caloriesSlide, macroIdx, macroItems, randomRecipe, isSpinning, pickRandom, recipes, baseRecipes, isLoadingBaseRecipes, randomMealFilter, mealFilters, filteredBaseRecipes, getBaseRecipeImage, randomRecipeDialog, openRandomRecipeDialog, addingToMyRecipes, addedToMyRecipes, addRandomToMyRecipes };
  },
};
</script>

<style scoped>
.dashboard { display: flex; flex-direction: column; gap: clamp(10px, 1.5vw, 16px); }

/* ===== STAT CARDS ===== */
.statsRow {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: clamp(6px, 1vw, 12px);
  align-items: stretch;
  margin-bottom: clamp(8px, 1.2vw, 12px);
}
.statCard { background: linear-gradient(180deg, #F5F1EC 0%, #FCFAFB 100%); border-radius: 16px; padding: clamp(10px, 1.2vw, 16px); box-shadow: none; border: 1px solid #E7E4E4; display: flex; flex-direction: column; justify-content: space-between; gap: 6px; min-height: 0; box-sizing: border-box; overflow: hidden; }
.statCard--poids { min-width: 0; }
.statCard--calories { min-width: 0; }
.statCard--menus { min-width: 0; }
.statCard--next { min-width: 0; }
.statCard--macros { min-width: 0; }
.statCard__header { display: flex; align-items: center; gap: 8px; }
.statCard__icon { width: 27px; height: 27px; border-radius: 50%; background: #ffffff; color: #1A1615; opacity: 0.55; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.statCard__label { font-size: clamp(11px, 1vw, 13px); font-weight: 600; color: #1A1615; opacity: 1; margin: 0; }
.statCard__main { display: flex; align-items: baseline; gap: 4px; }
.statCard__main--bottom { margin-top: 0; }
.statCard__value { font-size: clamp(1rem, 1.8vw, 1.3rem); font-weight: 700; letter-spacing: -0.03em; color: #1A1615; line-height: 1; }
.statCard__unit { font-size: clamp(0.7rem, 1vw, 0.85rem); opacity: 0.45; font-weight: 500; }
.statCard__sub { margin: 0; font-size: clamp(10px, 1vw, 12px); opacity: 0.45; }
.menusContent { display: flex; flex-direction: column; gap: 2px; }



/* Macros */
.statCard--nextmeal { flex: 2; min-width: 160px; }
.nextMeal { display: flex; gap: 10px; align-items: flex-start; margin-top: 0; }
.nextMeal__img { width: 52px; height: 52px; border-radius: 10px; overflow: hidden; background: rgba(117,83,252,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #7553FC; }
.nextMeal__img img { width: 100%; height: 100%; object-fit: cover; }
.nextMeal__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.nextMeal__title { margin: 0; font-weight: 700; font-size: clamp(11px, 1vw, 13px); color: #1A1615; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nextMeal__kcal { margin: 0; font-size: clamp(9px, 0.9vw, 11px); opacity: 0.45; font-weight: 500; }
.nextMeal__tags { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 2px; }
.nextMeal__tag { font-size: clamp(8px, 0.8vw, 10px); font-weight: 600; padding: 1px 7px; border-radius: 999px; background: rgba(117,83,252,0.1); color: #7553FC; }
.statCard--macros { gap: 8px; }
.macrosGrid { display: flex; flex-direction: row; gap: clamp(6px, 0.9vw, 10px); align-items: flex-start; flex-wrap: nowrap; overflow: hidden; }
.macroItem { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; flex-shrink: 0; }
.macroItem__val { font-size: clamp(0.85rem, 1.4vw, 1.1rem); font-weight: 700; letter-spacing: -0.02em; color: #1A1615; line-height: 1; }
.macroItem__unit { font-size: 0.65em; font-weight: 500; opacity: 0.7; }
.macroItem__unit--plain { font-size: clamp(0.7rem, 1vw, 0.85rem); font-weight: 500; opacity: 0.45; color: #1A1615; margin-left: 2px; }
.macroItem__row { display: flex; align-items: baseline; gap: 1px; }
.macroItem__label { font-size: clamp(9px, 0.9vw, 11px); opacity: 0.5; font-weight: 500; }

/* Hashtags */
.statCard--tags { min-width: 0; }
.profileTags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
.profileTag {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.4;
}

/* Crayon profil */
.statCard__editBtn {
  background: none;
  border: none;
  cursor: pointer;
  color: #1A1615;
  opacity: 0.3;
  padding: 2px;
  display: flex;
  align-items: center;
  transition: opacity 0.15s;
}
.statCard__editBtn:hover { opacity: 0.8; }

/* Barre progression poids */
.weightDisplay {
  display: flex;
  align-items: baseline;
  gap: 4px;
  cursor: pointer;
  margin-top: 4px;
}
.weightDisplay__value {
  font-size: clamp(1rem, 1.8vw, 1.3rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1A1615;
  line-height: 1;
}
.weightDisplay__unit {
  font-size: clamp(0.7rem, 1vw, 0.85rem);
  opacity: 0.45;
  font-weight: 500;
}
.weightDisplay__edit {
  opacity: 0.3;
  margin-left: 2px;
}
.weightBar {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.weightBar__track {
  height: 3px;
  border-radius: 999px;
  background: #E7E4E4;
  overflow: hidden;
}
.weightBar__fill {
  height: 100%;
  border-radius: 999px;
  background: #7553FC;
  transition: width 0.5s ease;
}
.weightBar__labels {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #1A1615;
  opacity: 0.4;
}
.weightInput {
  font-size: clamp(1rem, 1.8vw, 1.3rem);
  font-weight: 700;
  color: #1A1615;
  border: none;
  border-bottom: 2px solid #7553FC;
  outline: none;
  background: transparent;
  font-family: inherit;
  width: 70px;
  padding: 0;
}

/* Calories/Macros slide */
.calMacroRow { display: flex; align-items: flex-end; justify-content: space-between; gap: 6px; flex: 1; }
.macrosMasterRow { display: flex; align-items: flex-end; justify-content: space-between; gap: 6px; flex: 1; }
.slideArrow {
  background: #1A1615;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 15px;
  color: #F5F1EC;
  line-height: 1;
  flex-shrink: 0;
  font-family: inherit;
  opacity: 1;
  transition: opacity 0.15s;
  padding: 0;
}
.slideArrow:hover { opacity: 0.75; }

/* Macros carousel mobile */
.macrosGrid--desktop { display: flex; }
.macrosCarousel { display: none; align-items: center; gap: 6px; }
.macrosCarousel__arrow {
  background: none;
  border: 0.5px solid rgba(26,22,21,0.2);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: #1A1615;
  line-height: 1;
  flex-shrink: 0;
  font-family: inherit;
}
.macrosCarousel__arrow:hover { background: rgba(26,22,21,0.06); }

/* ===== MAIN ROW ===== */
.mainRow { display: grid; grid-template-columns: 2fr 1.5fr 1.5fr; gap: clamp(8px, 1.2vw, 12px); align-items: stretch; }
.leftCol { display: flex; flex-direction: column; gap: clamp(8px, 1.2vw, 12px); min-width: 0; }
.middleCol { display: flex; flex-direction: column; gap: clamp(8px, 1.2vw, 12px); min-width: 0; min-height: 0; max-height: 100%; }
.rightCol { display: flex; flex-direction: column; gap: clamp(8px, 1.2vw, 12px); min-width: 0; }

/* ===== CARD ===== */
.card { background: #ffffff; border-radius: 16px; padding: clamp(14px, 1.8vw, 20px); box-shadow: 0 4px 12px rgba(66,64,64,0.1); border: none; }
.card__header { display: flex; align-items: center; justify-content: space-between; margin-bottom: clamp(10px, 1.2vw, 14px); }
.card__title { margin: 0; font-weight: 600; font-size: clamp(11px, 1vw, 13px); color: #1A1615; }
.card__badge { font-size: clamp(10px, 0.9vw, 12px); font-weight: 700; padding: 2px 9px; border-radius: 999px; background: rgba(26,22,21,0.07); color: #1A1615; }
.linkBtn { background: none; border: none; cursor: pointer; font-size: clamp(11px, 1vw, 13px); font-weight: 400; color: #1A1615; display: inline-flex; align-items: center; gap: 2px; padding: 0; font-family: inherit; opacity: 0.55; }

/* Calendar */
.card--calendar { box-sizing: border-box; }
.weekCalendar { display: flex; gap: clamp(3px, 0.5vw, 6px); justify-content: space-between; }
.dayPill { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; padding: clamp(6px, 0.8vw, 10px) 2px; border-radius: 10px; cursor: pointer; transition: background 0.15s; border: 1.5px solid transparent; }
.dayPill:hover { background: rgba(117,83,252,0.05); }
.dayPill--today { border-color: #7553FC; }
.dayPill--today:not(.dayPill--selected):hover { background: rgba(117,83,252,0.05); border-color: transparent; }
.dayPill--selected { background: #F5F1EE; border-color: #F5F1EE; }
.dayPill--selected .dayPill__name { color: rgba(26,22,21,0.5); }
.dayPill--selected .dayPill__num { color: #1A1615 !important; font-weight: 800; }
.dayPill__name { font-size: clamp(9px, 0.8vw, 11px); font-weight: 600; text-transform: uppercase; opacity: 0.5; letter-spacing: 0.04em; }
.dayPill__num { font-size: clamp(0.8rem, 1.2vw, 1rem); font-weight: 700; color: #1A1615; }
.dayPill--today:not(.dayPill--selected) .dayPill__num { color: #7553FC; }

/* Meals */
.mealList { display: flex; flex-direction: column; gap: 10px; }
.mealSection__name { margin: 0 0 4px; font-size: clamp(9px, 0.8vw, 11px); font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.4; }
.mealRow { display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid rgba(0,0,0,0.04); }
.mealRow__img { width: 28px; height: 28px; border-radius: 6px; overflow: hidden; background: rgba(117,83,252,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #7553FC; }
.mealRow__img img { width: 100%; height: 100%; object-fit: cover; }
.mealRow__name { flex: 1; font-size: clamp(11px, 1vw, 13px); font-weight: 500; color: #1A1615; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mealRow__kcal { font-size: clamp(9px, 0.9vw, 11px); font-weight: 600; opacity: 0.4; flex-shrink: 0; }

/* Courses */
.toggleGroup { display: flex; gap: 4px; }
.toggleBtn { background: none; border: 1.5px solid rgba(0,0,0,0.1); border-radius: 999px; padding: 3px clamp(8px, 1vw, 12px); font-size: clamp(10px, 0.9vw, 12px); font-weight: 600; cursor: pointer; font-family: inherit; color: #1A1615; opacity: 0.5; transition: all 0.15s; }
.toggleBtn--active { border-color: #7553FC; color: #7553FC; opacity: 1; }
.card--courses { flex: 1; display: flex; flex-direction: column; min-height: 0; max-height: 100%; overflow: hidden; }
.coursesList { flex: 1; min-height: 0; display: flex; flex-direction: column; gap: 4px; max-height: 377px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #F5F1EE transparent; }
.coursesList::-webkit-scrollbar { width: 4px; }
.coursesList::-webkit-scrollbar-track { background: transparent; }
.coursesList::-webkit-scrollbar-thumb { background: #F5F1EE; border-radius: 999px; }
.courseItem { display: flex; align-items: center; gap: 4px; padding: 4px 0; border-bottom: 1px solid rgba(0,0,0,0.04); font-size: clamp(11px, 1vw, 13px); cursor: pointer; }
.courseItem__name { flex: 1; color: #1A1615; font-weight: 500; }
.courseItem__qty { font-size: clamp(9px, 0.9vw, 11px); opacity: 0.45; font-weight: 600; }
.courseItem--checked { opacity: 0.45; }
.courseItem--checked .courseItem__name { text-decoration: line-through; }
.courseItem__icon { color: #7553FC; flex-shrink: 0; }

/* Recipes */
.recipeList { display: flex; flex-direction: column; gap: 2px; }
.recipeRow { display: flex; align-items: center; gap: 10px; padding: 6px; border-radius: 8px; cursor: pointer; transition: background 0.15s; }
.recipeRow:hover { background: rgba(117,83,252,0.05); }
.recipeRow__img { width: 34px; height: 34px; border-radius: 7px; overflow: hidden; background: rgba(117,83,252,0.08); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #7553FC; }
.recipeRow__img img { width: 100%; height: 100%; object-fit: cover; }
.recipeRow__info { flex: 1; min-width: 0; }
.recipeRow__name { margin: 0 0 3px; font-weight: 600; font-size: clamp(11px, 1vw, 13px); color: #1A1615; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.recipeRow__tags { display: flex; gap: 4px; }
.tag { font-size: clamp(9px, 0.8vw, 11px); font-weight: 700; padding: 1px 7px; border-radius: 999px; display: inline-flex; align-items: center; gap: 2px; }
.tag--cat { background: rgba(117,83,252,0.1); color: #7553FC; }
.tag--kcal { background: rgba(255,120,50,0.1); color: #e06020; }

/* ══ Random recipe card — redesign ══ */
.randomCard {
  border-radius: 18px;
  min-height: 148px;
  position: relative;
  overflow: hidden;
  background: #faf9ff;
  border: 1px solid rgba(117,83,252,0.14);
  box-shadow: 0 1px 3px rgba(117,83,252,0.06), 0 4px 12px rgba(117,83,252,0.06);
  transition: box-shadow 0.2s, transform 0.2s;
}
.randomCard:hover { box-shadow: 0 4px 20px rgba(117,83,252,0.14); transform: translateY(-1px); }

/* Image décorative fixe en fond */
.randomCard__decoBg {
  position: absolute; inset: 0;
  background-image: url('https://recipecontent.fooby.ch/17429_3-2_1920-1280.jpg');
  background-size: cover; background-position: center;
  opacity: 0.75; transition: opacity 0.4s;
}
.randomCard--has-result .randomCard__decoBg,
.randomCard--spinning .randomCard__decoBg { opacity: 0; }
.randomCard__glass {
  position: absolute; inset: 0;
  background: linear-gradient(160deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.45) 100%);
  transition: opacity 0.4s;
}
.randomCard--has-result .randomCard__glass,
.randomCard--spinning .randomCard__glass { opacity: 0; }

.randomCard__content {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 16px; gap: 14px; text-align: center;
  min-height: 148px;
}

/* ── Filtres chips transparents ── */
.randomCard__filters {
  display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; width: 100%;
}
.randomCard__filterChip {
  font-size: clamp(10px, 0.9vw, 11px); font-weight: 400;
  font-family: 'OpenRunde', inherit;
  padding: 4px 11px; border-radius: 999px; cursor: pointer;
  border: 1px solid rgba(255,255,255,0.55);
  background: rgba(255,255,255,0.15);
  color: rgba(255,255,255,0.85);
  backdrop-filter: blur(4px);
  transition: all 0.15s; white-space: nowrap;
  text-shadow: 0 1px 4px rgba(0,0,0,0.3);
}
.randomCard__filterChip:hover { background: rgba(255,255,255,0.25); color: #fff; }
.randomCard__filterChip--active {
  background: rgba(255,255,255,0.9); color: #1A1615;
  border-color: transparent; text-shadow: none;
}

/* ── Bouton lancer ── */
.randomCard__spinBtn {
  background: linear-gradient(135deg, #7553FC 0%, #9370fd 100%);
  border: none; border-radius: 999px;
  padding: 7px 18px; font-size: clamp(11px, 0.95vw, 12px); font-weight: 400;
  color: #fff; cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
  font-family: 'OpenRunde', inherit; transition: all 0.15s;
  box-shadow: 0 3px 10px rgba(117,83,252,0.28);
  letter-spacing: 0.1px;
}
.randomCard__spinBtn:hover { box-shadow: 0 4px 16px rgba(117,83,252,0.4); transform: translateY(-1px); }
.randomCard__spinBtn:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }

/* ── Spinning ── */
.randomCard__spinLabel {
  margin: 0; font-size: clamp(11px, 1vw, 12px); font-weight: 600;
  color: rgba(255,255,255,0.85); letter-spacing: 0.1px;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
}

/* ── Résultat ── */
.randomCard__resultWrap {
  display: flex; align-items: center; gap: 11px;
  width: 100%; cursor: pointer; padding: 4px;
  border-radius: 12px; transition: background 0.15s;
}
.randomCard__resultWrap:hover { background: rgba(117,83,252,0.04); }
.randomCard__resultImg {
  width: 52px; height: 52px; border-radius: 12px; flex-shrink: 0; overflow: hidden;
  background: rgba(117,83,252,0.08);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.randomCard__resultImg img { width: 100%; height: 100%; object-fit: cover; display: block; }
.randomCard__resultImg--empty { display: flex; align-items: center; justify-content: center; }
.randomCard__resultInfo { flex: 1; min-width: 0; text-align: left; }
.randomCard__resultEyebrow {
  margin: 0 0 2px; font-size: clamp(9px, 0.78vw, 10px);
  font-weight: 600; color: #7553FC; text-transform: uppercase; letter-spacing: 0.5px;
}
.randomCard__resultName {
  margin: 0 0 5px; font-size: clamp(11px, 1.05vw, 13px); font-weight: 700; color: #1A1615;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
  line-height: 1.2; letter-spacing: -0.1px;
}.randomCard__resultTags { display: flex; gap: 4px; flex-wrap: wrap; }
.randomCard__tag {
  font-size: clamp(9px, 0.78vw, 10px); font-weight: 600; padding: 2px 7px;
  border-radius: 999px; background: rgba(117,83,252,0.1); color: #7553FC;
}
.randomCard__tag--kcal { background: rgba(255,120,50,0.1); color: #e06020; }

.randomCard__reroll {
  background: none; border: 1px solid rgba(117,83,252,0.2);
  border-radius: 999px; padding: 5px 13px; font-size: clamp(10px, 0.85vw, 11px);
  font-weight: 600; color: #7553FC; cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px; font-family: inherit; transition: all 0.15s;
}
.randomCard__reroll:hover { background: rgba(117,83,252,0.07); border-color: rgba(117,83,252,0.35); }

/* ══ Dialog recette aléatoire ══ */
.rdialog__hero {
  display: grid; grid-template-columns: 220px 1fr; gap: 0;
  border-radius: 24px 24px 0 0; overflow: hidden;
}
.rdialog__imgWrap {
  width: 220px; min-height: 260px; background: rgba(26,22,21,0.06);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.rdialog__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.rdialog__imgPlaceholder { width: 100%; height: 260px; display: flex; align-items: center; justify-content: center; }
.rdialog__heroInfo {
  padding: 24px 24px 20px; display: flex; flex-direction: column; gap: 12px;
  position: relative;
}
.rdialog__eyebrow {
  font-size: 10px; font-weight: 600; color: #7553FC;
  text-transform: uppercase; letter-spacing: 0.8px; margin: 0;
}
.rdialog__title {
  margin: 0; font-size: 22px; font-weight: 700; color: rgb(var(--v-theme-text));
  line-height: 1.2; letter-spacing: -0.3px;
}
.rdialog__tags { display: flex; gap: 6px; flex-wrap: wrap; }
.rdialog__tag {
  font-size: 11px; font-weight: 500; padding: 3px 11px; border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06); color: rgba(var(--v-theme-text), 0.6);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}
.rdialog__tag--accent {
  background: rgba(117,83,252,0.1); color: #7553FC;
  border-color: rgba(117,83,252,0.15); font-weight: 600;
}
.rdialog__macros { display: flex; gap: 16px; }
.rdialog__macroItem { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; }
.rdialog__macroVal { font-size: 18px; font-weight: 700; color: rgb(var(--v-theme-text)); line-height: 1; letter-spacing: -0.5px; }
.rdialog__macroLabel { font-size: 10px; font-weight: 500; color: rgba(var(--v-theme-text), 0.4); }
.rdialog__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: auto; }
.rdialog__btn {
  font-size: 12px; font-weight: 600; padding: 8px 16px; border-radius: 999px;
  border: none; cursor: pointer; display: inline-flex; align-items: center;
  font-family: inherit; transition: all 0.15s;
}
.rdialog__btn--primary { background: #7553FC; color: #fff; }
.rdialog__btn--primary:hover { background: #6340e8; }
.rdialog__btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.rdialog__btn--secondary {
  background: rgba(var(--v-theme-text), 0.07); color: rgba(var(--v-theme-text), 0.7);
}
.rdialog__btn--secondary:hover { background: rgba(var(--v-theme-text), 0.12); }
.rdialog__closeBtn {
  margin-left: auto; width: 30px; height: 30px; border-radius: 50%;
  background: rgba(var(--v-theme-text), 0.07); border: none;
  color: rgba(var(--v-theme-text), 0.5); display: flex; align-items: center;
  justify-content: center; cursor: pointer; transition: background 0.15s; flex-shrink: 0;
}
.rdialog__closeBtn:hover { background: rgba(var(--v-theme-text), 0.13); }
.rdialog__body { padding: 0 24px 24px !important; display: flex; flex-direction: column; gap: 20px; }
.rdialog__section { display: flex; flex-direction: column; gap: 12px; }
.rdialog__sectionTitle {
  margin: 0; font-size: 11px; font-weight: 700; color: rgba(var(--v-theme-text), 0.4);
  text-transform: uppercase; letter-spacing: 0.6px; display: flex; align-items: center; gap: 6px;
}
.rdialog__sectionCount {
  font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.07); color: rgba(var(--v-theme-text), 0.5);
}
.rdialog__ingGrid { display: flex; flex-wrap: wrap; gap: 12px 10px; }
.rdialog__ing {
  display: flex; flex-direction: column; align-items: center; gap: 4px; width: 72px;
}
.rdialog__ingCircle {
  width: 62px; height: 62px; border-radius: 50%;
  background: rgba(var(--v-theme-text), 0.05); border: 1px solid rgba(var(--v-theme-text), 0.08);
  display: flex; align-items: center; justify-content: center; overflow: hidden;
}
.rdialog__ingImg { width: 100%; height: 100%; object-fit: cover; }
.rdialog__ingName {
  font-size: 10px; font-weight: 500; color: rgb(var(--v-theme-text));
  text-align: center; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.rdialog__ingQty { font-size: 10px; color: rgba(var(--v-theme-text), 0.4); font-weight: 600; }
.rdialog__steps { display: flex; flex-direction: column; gap: 10px; }
.rdialog__step { display: flex; gap: 12px; align-items: flex-start; }
.rdialog__stepNum {
  width: 24px; height: 24px; border-radius: 50%; background: rgba(117,83,252,0.1);
  color: #7553FC; font-size: 11px; font-weight: 700; display: flex; align-items: center;
  justify-content: center; flex-shrink: 0; margin-top: 1px;
}
.rdialog__stepText { font-size: 13px; color: rgb(var(--v-theme-text)); line-height: 1.6; flex: 1; }

/* States */

.loadingArea { display: flex; justify-content: center; padding: 1.5rem 0; }
.emptyState { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 1.5rem 0; opacity: 0.4; font-size: clamp(11px, 1vw, 13px); text-align: center; }
.emptyState--sm { padding: 0.75rem 0; }

/* Responsive */
@media (max-width: 1100px) { .mainRow { grid-template-columns: 1.5fr 1fr; } .rightCol { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; } }
@media (max-width: 900px) {
  .statsRow { grid-template-columns: 1fr 1fr 1fr; }
  .mainRow { grid-template-columns: 1fr; }
}
@media (max-width: 900px) {
  .macrosGrid--desktop { display: none; }
  .macrosCarousel { display: flex; }
}
@media (max-width: 600px) {
  .statsRow {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    gap: 10px;
  }
  .statsRow::-webkit-scrollbar { display: none; }
  .statCard {
    flex: 0 0 72vw;
    scroll-snap-align: start;
  }
}
.card__header__left { display: flex; align-items: center; gap: 6px; }
.card__header__right { display: flex; align-items: center; gap: 8px; }
.card__icon { color: #1A1615; opacity: 0.55; flex-shrink: 0; }
.addItemBtn { background: none; border: 1px solid rgba(0,0,0,0.12); border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #1A1615; opacity: 0.5; transition: opacity 0.15s; }
.addItemBtn:hover { opacity: 1; }
.addItemRow { display: flex; gap: 6px; margin-bottom: 8px; }
.addItemInput { flex: 1; border: 1px solid #E7E4E4; border-radius: 8px; padding: 5px 10px; font-size: 12px; font-family: inherit; outline: none; background: #F9F7F8; }
.addItemInput:focus { border-color: #7553FC; }
.addItemConfirm { background: #7553FC; border: none; border-radius: 8px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: white; }
@media (max-width: 480px) { .statsRow { grid-template-columns: 1fr; } .statCard--nextmeal { flex: 1 1 100%; } }
</style>
